import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ShoppingBag, Store, Package, Users, BookOpen, Wallet, FileText, ReceiptText, Settings, LogOut, UserRound, PanelLeftClose, PanelLeftOpen, Menu, X, Sun, Moon } from 'lucide-react';
import { AppContext } from '../../App';
import { useAuth } from '../../contexts/AuthContext';
import { brandLogo as brandMark } from '../../config/brand';
import '../../styles/product.css';

const groups = [
  { label: 'Vue d’ensemble', items: [['/dashboard', 'Tableau de bord', LayoutDashboard]] },
  { label: 'Votre activité', items: [['/commandes', 'Ventes', ShoppingBag], ['/boutique', 'Boutiques', Store], ['/inventaire', 'Inventaire', Package], ['/crm', 'Clients', Users]] },
  { label: 'Vos finances', items: [['/facture', 'Factures', FileText], ['/comptabilite', 'Comptabilité', ReceiptText], ['/creances', 'Créances', Wallet]] },
  { label: 'Ressources', items: [['/bibliotheque', 'Bibliothèque', BookOpen]] }
];

export default function AppLayout({ children }) {
  const { user, darkMode, setDarkMode } = useContext(AppContext);
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('nsara_sidebar_collapsed') === 'true');
  const sidebar = useRef(null);
  const trigger = useRef(null);
  const accountMenu = useRef(null);
  const accountName = currentUser?.displayName || (user.nom !== 'Nouvel Utilisateur' ? user.nom : '') || 'Mon compte';
  const compact = collapsed && !mobile;
  const active = to => location.pathname === to || location.pathname.startsWith(`${to}/`);
  useEffect(() => {
    const resize = () => { setMobile(window.innerWidth < 768); if (window.innerWidth >= 768) setOpen(false); };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  useEffect(() => { localStorage.setItem('nsara_sidebar_collapsed', collapsed); }, [collapsed]);
  useEffect(() => { setOpen(false); setAccountMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onPointerDown = event => { if (!accountMenu.current?.contains(event.target)) setAccountMenuOpen(false); };
    const onKeyDown = event => { if (event.key === 'Escape') setAccountMenuOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('mousedown', onPointerDown); document.removeEventListener('keydown', onKeyDown); };
  }, [accountMenuOpen]);
  useEffect(() => {
    if (!open || !mobile) return undefined;
    const oldOverflow = document.body.style.overflow;
    const triggerEl = trigger.current;
    document.body.style.overflow = 'hidden';
    sidebar.current?.querySelector('button')?.focus();
    const keys = event => {
      if (event.key === 'Escape') setOpen(false);
      if (event.key === 'Tab') {
        const targets = [...sidebar.current.querySelectorAll('a, button')].filter(el => el.offsetParent !== null);
        const first = targets[0], last = targets[targets.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', keys);
    return () => { document.body.style.overflow = oldOverflow; document.removeEventListener('keydown', keys); triggerEl?.focus(); };
  }, [open, mobile]);
  const navItem = (to, label, Icon) => <Link key={to} to={to} className={`product-nav-link${active(to) ? ' is-active' : ''}`} aria-current={active(to) ? 'page' : undefined} aria-label={label} title={compact ? label : undefined}>
    <Icon size={19} aria-hidden="true"/><span className="nav-copy">{label}</span>
  </Link>;
  return <div className={`product-shell${compact ? ' is-collapsed' : ''}`} data-theme={darkMode ? 'dark' : 'light'}>
    <a className="skip-link" href="#main-content">Aller au contenu</a>
    {open && mobile && <button className="nav-backdrop" aria-label="Fermer la navigation" onClick={() => setOpen(false)}/>}
    <aside id="product-navigation" ref={sidebar} className={`product-sidebar${open ? ' is-open' : ''}`} aria-label="Navigation principale" role={mobile && open ? 'dialog' : undefined} aria-modal={mobile && open ? true : undefined}>
      <div className="product-brand"><Link to="/" aria-label="NSARA — accueil"><img src={brandMark} alt="" width="38" height="38"/><span className="nav-copy brand-word">nsara<span>Votre activité, simplement.</span></span></Link>
        {mobile && <button className="icon-button sidebar-close" aria-label="Fermer le menu" onClick={() => setOpen(false)}><X size={20}/></button>}
      </div>
      <nav>{groups.map(group => <div className="product-nav-group" key={group.label}><p className="nav-section-label">{group.label}</p>{group.items.map(item => navItem(...item))}</div>)}</nav>
      <div className="sidebar-bottom">
        {!mobile && <button className="product-nav-link" onClick={() => setCollapsed(v => !v)} aria-label={compact ? 'Agrandir la navigation' : 'Réduire la navigation'}>{compact ? <PanelLeftOpen size={19}/> : <PanelLeftClose size={19}/>}<span className="nav-copy">Réduire le menu</span></button>}
        <div className="account-menu-anchor" ref={accountMenu}>
          {accountMenuOpen && (
            <div className="account-menu" role="menu" aria-label="Menu du compte">
              <Link role="menuitem" to="/profil" onClick={() => setAccountMenuOpen(false)}><UserRound size={16}/> Mon profil</Link>
              <Link role="menuitem" to="/parametres" onClick={() => setAccountMenuOpen(false)}><Settings size={16}/> Paramètres</Link>
              <button role="menuitem" onClick={() => { setAccountMenuOpen(false); logout(); }}><LogOut size={16}/> Se déconnecter</button>
            </div>
          )}
          <button
            className="sidebar-account"
            onClick={() => setAccountMenuOpen(v => !v)}
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
            aria-label={`Compte de ${accountName}`}
          >
            <span className="account-avatar">{accountName.charAt(0).toUpperCase()}</span>
            <div className="nav-copy"><strong>{accountName}</strong><span>Votre espace de gestion</span></div>
          </button>
        </div>
      </div>
    </aside>
    <div className="product-workspace">
      <header className="product-topbar"><div className="topbar-context">{mobile && <button ref={trigger} className="icon-button" aria-label="Ouvrir la navigation" aria-expanded={open} aria-controls="product-navigation" onClick={() => setOpen(true)}><Menu size={22}/></button>}<span className="workspace-dot"/><span>{user.nomEntreprise || 'Mon espace NSARA'}</span></div><div className="topbar-account"><span className="topbar-label">Espace de gestion</span><button className="icon-button" onClick={() => setDarkMode(!darkMode)} aria-label={darkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}>{darkMode ? <Sun size={19}/> : <Moon size={19}/>}</button></div></header>
      <main id="main-content" tabIndex="-1" className="product-content">{children}</main>
    </div>
  </div>;
}
